import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import DashboardLayout from '../components/DashboardLayout';
import { Plus, TrendingUp, Calendar as CalendarIcon, Trash2, ArrowLeft, RefreshCw, Sparkles } from 'lucide-react';
import { incomeAPI, recurringAPI } from '../services/apiServices';
import { toast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../components/ui/popover";
import { format, startOfMonth, endOfMonth, isWithinInterval, parseISO } from "date-fns";
import { cn } from "../lib/utils";

const Income = () => {
    const [incomes, setIncomes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isRecurringDialogOpen, setIsRecurringDialogOpen] = useState(false);
    const [recurringList, setRecurringList] = useState([]);

    // Date-wise filtering
    const [dateRange, setDateRange] = useState({
        from: startOfMonth(new Date()),
        to: endOfMonth(new Date()),
    });

    const [newIncome, setNewIncome] = useState({
        source: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        isRecurring: false,
        frequency: 'monthly'
    });

    useEffect(() => {
        fetchIncome();
        fetchRecurring();
    }, []);

    const fetchIncome = async () => {
        try {
            setLoading(true);
            const data = await incomeAPI.getIncome();
            setIncomes(data || []);
        } catch (error) {
            console.error("Error fetching income:", error);
            toast({ title: 'Error', description: 'Failed to load income history', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const fetchRecurring = async () => {
        try {
            const data = await recurringAPI.getRecurring();
            // Filter only income types
            setRecurringList(data?.filter(r => r.type === 'income') || []);
        } catch (e) { console.warn("Fetch recurring failure", e); }
    };

    const handleAddIncome = async (e) => {
        e.preventDefault();
        if (!newIncome.source || !newIncome.amount) return;

        try {
            // 1. Add to Ledger
            await incomeAPI.addIncome({
                source: newIncome.source,
                amount: Number(newIncome.amount),
                date: newIncome.date,
                description: newIncome.description,
                type: 'income'
            });

            // 2. Add to Automation Engine if Recurring
            if (newIncome.isRecurring) {
                await recurringAPI.createRecurring({
                    description: newIncome.source,
                    amount: Number(newIncome.amount),
                    frequency: newIncome.frequency,
                    start_date: newIncome.date,
                    type: 'income'
                });
                fetchRecurring();
            }

            toast({ title: 'Success', description: newIncome.isRecurring ? 'Income & Automation set' : 'Income added successfully' });
            setShowModal(false);
            setNewIncome({
                source: '',
                amount: '',
                date: new Date().toISOString().split('T')[0],
                description: '',
                isRecurring: false,
                frequency: 'monthly'
            });
            fetchIncome();
        } catch (error) {
            console.error("Error adding income:", error);
            toast({ title: 'Error', description: 'Failed to add income', variant: 'destructive' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this entry?")) return;
        try {
            await incomeAPI.deleteIncome(id);
            setIncomes(prev => prev.filter(i => i.id !== id));
            toast({ title: 'Deleted', description: 'Income entry removed' });
        } catch (error) {
            toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
        }
    };

    const handleToggleRecurring = async (id, currentStatus) => {
        try {
            await recurringAPI.toggleRecurring(id, !currentStatus);
            fetchRecurring();
            toast({ title: "Rule Updated", description: "Automation status toggled." });
        } catch (e) {
            toast({ title: "Update Failed", variant: "destructive" });
        }
    };

    const handleDeleteRecurring = async (id) => {
        try {
            await recurringAPI.deleteRecurring(id);
            fetchRecurring();
            toast({ title: "Rule Removed", description: "Automation stopped for this source." });
        } catch (e) {
            toast({ title: "Delete Failed", variant: "destructive" });
        }
    };

    // Filter Logic
    const filteredIncomes = incomes.filter(inc => {
        const d = parseISO(inc.date);
        return isWithinInterval(d, { start: dateRange.from, end: dateRange.to || dateRange.from });
    });

    const totalIncomeForMonth = filteredIncomes.reduce((sum, inc) => sum + Number(inc.amount), 0);

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    return (
        <DashboardLayout>
            <div className="p-4 md:p-8 max-w-6xl mx-auto animate-in fade-in duration-500 min-h-screen bg-black">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <div className="flex items-center gap-4 mb-2">
                            <Link to="/dashboard" className="p-2 hover:bg-white/5 rounded-full transition-colors">
                                <ArrowLeft size={20} className="text-slate-400" />
                            </Link>
                            <div className="flex items-center gap-2 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 w-fit">
                                <Sparkles size={10} className="text-emerald-400" />
                                <span className="text-[8px] font-black uppercase tracking-[0.3em] text-emerald-400">FinAssure Liquidity Index</span>
                            </div>
                        </div>
                        <h1 className="text-2xl md:text-3xl font-black text-white tracking-tighter capitalize ml-12">Income Pulse</h1>
                        <p className="text-slate-400 ml-12 text-sm font-medium tracking-tight">"Orchestrate your cash flow systems with precision."</p>
                    </div>
                    <div className="flex gap-2 w-full md:w-auto">
                        <Button
                            onClick={() => setIsRecurringDialogOpen(true)}
                            className="flex-1 md:flex-none bg-black hover:bg-white hover:text-black border border-white/10 text-white font-bold h-12 px-6 rounded-xl transition-all flex items-center justify-center gap-2"
                        >
                            <RefreshCw size={18} />
                            Automation
                        </Button>
                        <Button
                            onClick={() => setShowModal(true)}
                            className="flex-3 md:flex-none bg-emerald-600 hover:bg-emerald-500 text-white font-black h-12 px-8 rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.1)] hover:shadow-emerald-500/20 transition-all flex items-center justify-center gap-2"
                        >
                            <Plus size={20} />
                            Record Income
                        </Button>
                    </div>
                </div>

                {/* Stats & Filter Bar */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
                    <Card className="p-4 md:p-6 bg-emerald-500/5 border-emerald-500/10 md:col-span-2 relative overflow-hidden group">
                        <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-emerald-500/5 rounded-full blur-3xl group-hover:bg-emerald-500/10 transition-all"></div>
                        <div className="flex justify-between items-start relative z-10">
                            <div>
                                <h2 className="text-[11px] font-black text-emerald-500 tracking-widest mb-1">Total Yield (Current Range)</h2>
                                <p className="text-3xl md:text-4xl font-black text-white mt-2">₹{totalIncomeForMonth.toLocaleString('en-IN')}</p>
                            </div>
                            <div className="p-3 bg-white/5 rounded-xl border border-white/5">
                                <TrendingUp className="text-emerald-400" size={32} />
                            </div>
                        </div>
                    </Card>

                    <Card className="p-4 md:p-6 flex flex-col justify-center bg-zinc-900/50 border-white/5">
                        <label className="text-[11px] font-black text-slate-400 tracking-widest mb-3 uppercase">Sync timeframe</label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant={"outline"}
                                    className={cn(
                                        "w-full justify-between text-left font-black h-12 bg-[#020617] border-white/5 text-[13px] px-4 rounded-xl hover:bg-white/5 hover:border-white/10 transition-all",
                                        !dateRange && "text-muted-foreground"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <CalendarIcon className="h-4 w-4 text-violet-500" />
                                        <span className="tracking-widest">
                                            {dateRange?.from ? format(dateRange.from, "dd-MM-yyyy") : "dd-mm-yyyy"}
                                        </span>
                                    </div>
                                    <span className="text-slate-700 font-bold">—</span>
                                    <div className="flex items-center gap-3">
                                        <span className="tracking-widest">
                                            {dateRange?.to ? format(dateRange.to, "dd-MM-yyyy") : "dd-mm-yyyy"}
                                        </span>
                                        <CalendarIcon className="h-4 w-4 text-slate-700" />
                                    </div>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-black border-white/10" align="end">
                                <Calendar
                                    initialFocus
                                    mode="range"
                                    defaultMonth={dateRange?.from}
                                    selected={dateRange}
                                    onSelect={setDateRange}
                                    numberOfMonths={2}
                                    className="bg-black text-white"
                                />
                            </PopoverContent>
                        </Popover>
                    </Card>
                </div>

                {/* History List */}
                <Card className="shadow-2xl border-white/5 bg-zinc-950/50 overflow-hidden backdrop-blur-xl">
                    <div className="p-4 md:p-6 border-b border-white/5 bg-white/[0.02] flex justify-between items-center">
                        <h3 className="font-black text-white text-xs tracking-widest flex items-center gap-2">
                            <CalendarIcon size={14} className="text-emerald-400" />
                            Transaction Sync
                        </h3>
                        <div className="md:hidden text-[11px] font-black text-slate-400">Swipe →</div>
                    </div>

                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-black border-b border-white/5">
                                <tr>
                                    <th className="px-6 py-4 text-[12px] font-black text-slate-400 tracking-widest uppercase">Date</th>
                                    <th className="px-6 py-4 text-[12px] font-black text-slate-400 tracking-widest uppercase">Source Entity</th>
                                    <th className="px-6 py-4 text-[12px] font-black text-slate-400 tracking-widest uppercase">Description</th>
                                    <th className="px-6 py-4 text-right text-[12px] font-black text-slate-400 tracking-widest uppercase">Amount</th>
                                    <th className="px-6 py-4 text-center text-[12px] font-black text-slate-400 tracking-widest uppercase">Control</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {loading ? (
                                    <tr><td colSpan="5" className="p-12 text-center text-slate-400 text-xs font-black uppercase tracking-widest animate-pulse">Syncing Records...</td></tr>
                                ) : filteredIncomes.length === 0 ? (
                                    <tr><td colSpan="5" className="p-12 text-center text-slate-400 text-xs font-black tracking-widest uppercase">No data for selected cycle</td></tr>
                                ) : (
                                    filteredIncomes.map((inc) => (
                                        <tr key={inc.id} className="hover:bg-white/[0.01] transition-colors group">
                                            <td className="px-6 py-5 text-[11px] font-black text-slate-500 uppercase tracking-tighter">
                                                {new Date(inc.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </td>
                                            <td className="px-6 py-5">
                                                <div className="text-sm font-black text-white italic">{inc.source || inc.category}</div>
                                            </td>
                                            <td className="px-6 py-5 text-xs text-slate-400 font-medium">{inc.description || '-'}</td>
                                            <td className="px-6 py-5 text-right text-sm font-black text-emerald-400">+₹{Number(inc.amount).toLocaleString('en-IN')}</td>
                                            <td className="px-6 py-5 text-center">
                                                <button onClick={() => handleDelete(inc.id)} className="p-2 text-slate-700 hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100">
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>

                {/* Add Modal */}
                {showModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl animate-in fade-in transition-all">
                        <div className="bg-black border border-white/10 rounded-[2rem] w-full max-w-md shadow-2xl overflow-hidden">
                            <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                                <h3 className="text-xl font-black text-white tracking-tighter italic">Record entry</h3>
                                <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-white transition-colors">✕</button>
                            </div>
                            <form onSubmit={handleAddIncome} className="p-8 space-y-5">
                                <div className="space-y-2">
                                    <label className="text-[11px] font-black text-slate-400 tracking-widest ml-1 uppercase italic">Transfer date</label>
                                    <input
                                        type="date"
                                        required
                                        className="w-full p-4 bg-zinc-900 border border-white/10 rounded-2xl text-white outline-none focus:ring-1 focus:ring-emerald-500/50 text-sm font-bold"
                                        value={newIncome.date}
                                        onChange={e => setNewIncome({ ...newIncome, date: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-500 tracking-widest ml-1 italic">Income source</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Monthly Salary"
                                        required
                                        className="w-full p-4 bg-zinc-900 border border-white/10 rounded-2xl text-white placeholder:text-slate-700 outline-none focus:ring-1 focus:ring-emerald-500/50 text-sm font-bold"
                                        value={newIncome.source}
                                        onChange={e => setNewIncome({ ...newIncome, source: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-500 tracking-widest ml-1 italic">Yield amount (₹)</label>
                                    <input
                                        type="number"
                                        placeholder="0.00"
                                        required
                                        className="w-full p-4 bg-zinc-900 border border-white/10 rounded-2xl text-white placeholder:text-slate-700 outline-none focus:ring-1 focus:ring-emerald-500/50 text-sm font-black"
                                        value={newIncome.amount}
                                        onChange={e => setNewIncome({ ...newIncome, amount: e.target.value })}
                                    />
                                </div>

                                <Card className="p-4 bg-emerald-500/5 border-emerald-500/10 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <label className="text-[11px] font-black text-emerald-500 tracking-widest uppercase italic">Automate stream?</label>
                                        <Switch checked={newIncome.isRecurring} onCheckedChange={c => setNewIncome({ ...newIncome, isRecurring: c })} />
                                    </div>
                                    {newIncome.isRecurring && (
                                        <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                                            <p className="text-[8px] font-black text-emerald-600 mb-2">Select frequency cycle</p>
                                            <div className="grid grid-cols-3 gap-2">
                                                {['daily', 'weekly', 'monthly'].map(f => (
                                                    <button
                                                        key={f}
                                                        type="button"
                                                        onClick={() => setNewIncome({ ...newIncome, frequency: f })}
                                                        className={`p-2 text-[11px] font-black rounded-lg border transition-all ${newIncome.frequency === f ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-transparent border-white/5 text-slate-400'}`}
                                                    >
                                                        {f}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Card>

                                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black py-5 rounded-2xl mt-4 tracking-widest shadow-lg shadow-emerald-500/10 text-xs italic">
                                    Execute entry
                                </Button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Recurring Rules Dialog */}
                <Dialog open={isRecurringDialogOpen} onOpenChange={setIsRecurringDialogOpen}>
                    <DialogContent className="bg-black border-white/10 text-white max-w-2xl rounded-3xl">
                        <DialogHeader className="flex flex-row items-center justify-between border-b border-white/5 pb-4">
                            <DialogTitle className="text-xl font-black tracking-tighter italic">Income automation</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-6">
                            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                                {recurringList.map(r => (
                                    <div key={r.id} className="flex justify-between items-center p-4 bg-zinc-950/50 hover:bg-emerald-500/5 rounded-2xl border border-white/5 hover:border-emerald-500/20 transition-all group">
                                        <div>
                                            <p className="font-black text-slate-100 italic">{r.description}</p>
                                            <p className="text-emerald-500/50 text-[10px] font-black tracking-widest mt-1 italic">{r.frequency} • ₹{r.amount}</p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <Switch checked={r.is_active} onCheckedChange={() => handleToggleRecurring(r.id, r.is_active)} />
                                            <button onClick={() => handleDeleteRecurring(r.id)} className="text-slate-700 hover:text-rose-500 transition-colors">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {recurringList.length === 0 && (
                                    <div className="text-center py-12 border border-dashed border-white/5 rounded-2xl">
                                        <p className="text-slate-600 font-black text-xs tracking-widest">No active revenue automations</p>
                                    </div>
                                )}
                            </div>
                            <Button
                                onClick={() => {
                                    setIsRecurringDialogOpen(false);
                                    setNewIncome(prev => ({ ...prev, isRecurring: true }));
                                    setShowModal(true);
                                }}
                                className="w-full h-14 bg-emerald-600 hover:bg-emerald-500 text-white font-black rounded-2xl flex items-center justify-center gap-2 transition-all tracking-widest text-[11px] italic"
                            >
                                <Plus size={18} /> Initialize new stream
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

            </div>
        </DashboardLayout>
    );
};

export default Income;
